import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '../../services/axiosClient';
import '../assets/css/modern-offers.css';

// Petite fonction utilitaire pour lire le token du localStorage s'il existe
function getAuthHeader() {
  try {
    const str = localStorage.getItem('userDetails');
    if (!str) return {};
    const { token, tokenType } = JSON.parse(str) || {};
    if (token) {
      return { Authorization: `${tokenType || 'Bearer'} ${token}` };
    }
    return {};
  } catch (_) {
    return {};
  }
}

function OfferItem({ offer }) {
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.stopPropagation();
    if (saving) return;
    try {
      const auth = getAuthHeader();
      if (!auth.Authorization) {
        alert('Vous devez être connecté pour sauvegarder une offre');
        return;
      }
      setSaving(true);
      const res = await axiosClient.post(
        `/posts/${offer.id}/save`,
        {},
        {
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', ...auth },
        }
      );
      if (res?.data?.success) {
        setIsSaved(true);
      } else {
        alert(res?.data?.message || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        // déjà sauvegardé
        setIsSaved(true);
      } else if (err?.response?.status === 401) {
        alert('Session expirée. Veuillez vous reconnecter.');
      } else {
        console.error('[OfferItem] save error:', err);
        alert("Impossible de sauvegarder l'offre");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/job-detail?id=${offer.id}`;
    if (navigator.share) {
      navigator.share({ title: offer.title, text: offer.org, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url);
      alert('Lien copié dans le presse-papiers');
    }
  };

  return (
      <div className="modern-offer-card position-relative overflow-hidden mb-4">
        {/* Gradient overlay for premium feel */}
        <div className="offer-gradient-overlay"></div>
        
        <div className="card-content p-4">
          <div className="d-flex align-items-start gap-4">
            {/* Enhanced Avatar with glow effect */}
            <div className="offer-avatar-container position-relative">
              <div className="offer-avatar">
                {offer.logoUrl ? (
                  <img
                    src={offer.logoUrl}
                    alt={offer.org || 'Logo'}
                    className="offer-logo"
                  />
                ) : (
                  <div className="offer-initials">
                    {(offer.org || '??').substring(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="offer-avatar-glow"></div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-grow-1 min-w-0">
              <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-lg-between gap-3 mb-3">
                <div className="offer-title-section">
                  <Link to={`/job-detail?id=${offer.id}`} className="text-decoration-none">
                    <h4 className="offer-title-modern mb-1 clickable-title">
                      {offer.title}
                    </h4>
                  </Link>
                  <div className="offer-company">
                    <i className="fa-regular fa-building me-2"></i>
                    {offer.org}
                  </div>
                </div>
                {/* <div className="offer-badges">
                  <span className="modern-badge category-badge">
                    {offer.category}
                  </span>
                  <span className="modern-badge type-badge">
                    {offer.type}
                  </span>
                </div> */}
              </div>
              
              <div className="offer-meta-modern mb-3">
                <div className="meta-item">
                  <i className="fa-solid fa-location-dot"></i>
                  <span>{offer.location}</span>
                </div>
                <div className="meta-item">
                  <i className="fa-regular fa-calendar"></i>
                  <span>{offer.date}</span>
                </div>
              </div>
              
              <p className="offer-excerpt">
                {offer.excerpt}
              </p>

              {/* Modern Action Buttons */}
              <div className="offer-actions">
                <button
                  type="button"
                  className={`action-btn primary-action ${isSaved ? 'active' : ''}`}
                  onClick={handleSave}
                  disabled={saving}
                >
                  <i className="fa-regular fa-bookmark"></i>
                  <span>{saving ? 'Sauvegarde…' : isSaved ? 'Sauvegardé' : 'Sauvegarder'}</span>
                </button>
                <button type="button" className="action-btn secondary-action" onClick={handleShare}>
                  <i className="fa-regular fa-share"></i>
                  <span>Partager</span>
                </button>
                <Link to={`/job-detail?id=${offer.id}`} className="action-btn tertiary-action text-decoration-none">
                  <i className="fa-solid fa-arrow-right"></i>
                  <span>Voir détails</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        {/* Hover indicator */}
        <div className="offer-hover-indicator"></div>
      </div>
  );
}

export default function RecentOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cities, setCities] = useState([]);
  const [sectionTitle, setSectionTitle] = useState('Offres récentes');
  const [sectionLink, setSectionLink] = useState('/posts');

  // Fonction pour récupérer les villes du Sénégal
  const fetchCities = async () => {
    try {
      const response = await axiosClient.get('/countries/SN/cities?perPage=100', {
        headers: { 
          'Accept': 'application/json',
          ...getAuthHeader() 
        },
      });
      
      if (response.status === 200) {
        const data = response.data;
        let cityList = data?.result?.data || data?.data || data?.result || data || [];
        if (!Array.isArray(cityList)) {
          if (cityList && Array.isArray(cityList.data)) cityList = cityList.data;
          else cityList = [];
        }
        
        // Normaliser les données des villes
        const normalizedCities = cityList.map(city => ({
          id: city.id,
          name: city.name,
          country_code: city.country_code,
          latitude: city.latitude,
          longitude: city.longitude,
          population: city.population,
          posts_count: city.posts_count || 0
        }));
        
        setCities(normalizedCities);
      }
    } catch (e) {
      console.error('[RecentOffers] Cities API fetch error:', e);
      // Fallback avec villes par défaut
      setCities([
        { id: 1, name: 'Dakar', country_code: 'SN', latitude: 14.69, longitude: -17.44, population: 1030594, posts_count: 0 },
        { id: 2, name: 'Thiès', country_code: 'SN', latitude: 14.79, longitude: -16.93, population: 320000, posts_count: 0 },
        { id: 3, name: 'Saint-Louis', country_code: 'SN', latitude: 16.02, longitude: -16.48, population: 176000, posts_count: 0 },
        { id: 4, name: 'Kaolack', country_code: 'SN', latitude: 14.15, longitude: -16.07, population: 233708, posts_count: 0 },
        { id: 5, name: 'Ziguinchor', country_code: 'SN', latitude: 12.58, longitude: -16.27, population: 230000, posts_count: 0 },
        { id: 6, name: 'Tambacounda', country_code: 'SN', latitude: 13.77, longitude: -13.67, population: 78800, posts_count: 0 }
      ]);
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    let mounted = true;
    async function fetchOffers() {
      try {
        setLoading(true);
        const response = await axiosClient.get('/homeSections/getLatestListings', {
          params: { fetchData: 1 },
          headers: { 
            Accept: 'application/json', 
            'X-Skip-Auth': true
          },
        });
        // Debug réseau
        console.debug('[RecentOffers] GET /homeSections/getLatestListings status:', response?.status);
        console.debug('[RecentOffers] payload sample:', response?.data);
        
        // Parse getLatestListings response format
        const data = response?.data;
        const latest = data?.result?.data?.latest || {};
        let items = Array.isArray(latest.posts) ? latest.posts : [];
        
        console.debug('[RecentOffers] Parsed items:', items);
        console.debug('[RecentOffers] Latest title:', latest.title);
        console.debug('[RecentOffers] Latest link:', latest.link);
        // Normalisation avec les champs corrects de l'API
        const mapped = items.slice(0, 10).map((it, index) => {
          // Utiliser les données de ville depuis l'API ou fallback
          const cityName = it.city?.name || cities.find(city => city.id === it.city_id)?.name || 'Dakar';
          
          // Gestion des logos avec conversion HTTPS->HTTP pour localhost
          let logoUrl = '';
          if (it.logo_url) {
            logoUrl = it.logo_url.small || it.logo_url.medium || it.logo_url.full;
            if (logoUrl && logoUrl.includes('https://localhost:8000')) {
              logoUrl = logoUrl.replace('https://localhost:8000', 'http://localhost:8000');
            }
          } else if (it.logo) {
            logoUrl = `http://localhost:8000/storage/${it.logo}`;
          }
          
          return {
            id: it.id,
            org: it.company_name || it.company?.name || '—',
            title: it.title || '—',
            category: it.category?.name || '—',
            location: cityName,
            type: it.postType?.name || '—',
            date: it.created_at_formatted || '',
            excerpt: it.excerpt || '',
            logoUrl: logoUrl,
          };
        });
        
        if (mounted) {
          setOffers(mapped);
          // Mettre à jour le titre et le lien de la section
          if (latest.title) {
            setSectionTitle(latest.title.replace(/<[^>]*>/g, '')); // Supprimer les balises HTML
          }
          // Toujours utiliser /posts au lieu du lien de l'API
          setSectionLink('/posts');
        }
      } catch (e) {
        // Debug erreur
        console.error('[RecentOffers] fetch error:', e);
        const msg = e?.response?.data?.message
          || e?.message
          || 'Erreur réseau';
        if (mounted) setError(`Impossible de charger les offres récentes: ${msg}`);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchOffers();
    return () => { mounted = false; };
  }, [cities]);

  return (
    <section className="recent-offers-section">
      <div className="section-header">
        <div className="header-content">
          <div className="header-badge">
            <i className="fa-solid fa-sparkles"></i>
            <span>Nouvelles opportunités</span>
          </div>
          <h2 className="section-title">
            {sectionTitle}
            <div className="title-underline"></div>
          </h2>
          <p className="section-subtitle">
            Découvrez les dernières opportunités d'Appels d'offres publiées par nos partenaires
          </p>
        </div>
        <Link to="/posts" className="view-all-btn">
          <span>Voir toutes les offres</span>
          <i className="fa-solid fa-arrow-right"></i>
        </Link>
      </div>

      {loading && <div className="text-muted">Chargement…</div>}
      {error && !loading && <div className="text-danger">{error}</div>}
      {!loading && !error && (
        <div>
          {offers.length === 0 && (
            <div className="text-muted">Aucune offre disponible pour le moment.</div>
          )}
          {offers.map((o) => (
            <OfferItem key={o.id} offer={o} />
          ))}
        </div>
      )}

      {/* Modern Footer CTA */}
      <div className="offers-footer">
        <div className="footer-decoration"></div>
        <Link to={sectionLink} className="explore-more-btn">
          <div className="btn-content">
            <span className="btn-text">Explorer plus d'opportunités</span>
            <div className="btn-icon">
              <i className="fa-solid fa-rocket"></i>
            </div>
          </div>
          <div className="btn-glow"></div>
        </Link>
      </div>
    </section>
  );
}
